"use client";
import {
  AlertOctagon,
  AlertTriangle,
  ChevronRight,
  FileCode2,
  Info,
  Search,
  Shield,
  X,
} from "lucide-react";
import {
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  type Finding,
  type ScanReport,
  type Severity,
} from "../../../lib/types";
import {
  severityOrder,
} from "../../shared/format";


export function FindingsView({
  report,
  selected,
  setSelected,
}: {
  report: ScanReport;
  selected: Finding | null;
  setSelected: (finding: Finding) => void;
}) {
  const [severity, setSeverity] = useState<Severity | "all">("all");
  const [query, setQuery] = useState("");
  const findings = useMemo(
    () =>
      report.findings.filter(
        (finding) =>
          (severity === "all" || finding.severity === severity) &&
          (!query ||
            [finding.title, finding.entry, finding.ruleId, finding.category]
              .join(" ")
              .toLowerCase()
              .includes(query.toLowerCase())),
      ),
    [query, report.findings, severity],
  );

  useEffect(() => {
    if ((!selected || !findings.includes(selected)) && findings[0]) {
      setSelected(findings[0]);
    }
  }, [findings, selected, setSelected]);

  return (
    <div className="full-height-view">
      <div className="view-toolbar">
        <div className="search-field">
          <Search size={15} />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search findings, files, or rules"
            aria-label="Search findings"
          />
          {query && (
            <button type="button" onClick={() => setQuery("")} aria-label="Clear">
              <X size={13} />
            </button>
          )}
        </div>
        <div className="severity-filters" aria-label="Filter by severity">
          <button
            className={severity === "all" ? "is-active" : ""}
            type="button"
            onClick={() => setSeverity("all")}
          >
            All <span>{report.findings.length}</span>
          </button>
          {severityOrder.slice(0, 4).map((item) => (
            <button
              key={item}
              className={`${severity === item ? "is-active" : ""} tone-${item}`}
              type="button"
              onClick={() => setSeverity(item)}
            >
              {item} <span>{report.counts[item]}</span>
            </button>
          ))}
        </div>
      </div>
      <div className="findings-layout">
        <div className="findings-list" aria-label="Detection findings">
          <div className="list-caption">
            <span>{findings.length} results</span>
            <span>sorted by severity</span>
          </div>
          {findings.map((finding) => (
            <button
              key={finding.id}
              type="button"
              className={selected?.id === finding.id ? "is-selected" : ""}
              onClick={() => setSelected(finding)}
            >
              <span className={`finding-marker tone-${finding.severity}`}>
                {finding.severity === "critical" ? (
                  <AlertOctagon size={16} />
                ) : (
                  <AlertTriangle size={16} />
                )}
              </span>
              <span className="finding-list-copy">
                <span>
                  <strong>{finding.title}</strong>
                  <small className={`severity-label tone-${finding.severity}`}>
                    {finding.severity}
                  </small>
                </span>
                <span className="path-line">{finding.entry}</span>
                <span className="finding-meta">
                  {finding.ruleId} · {finding.confidence} confidence
                </span>
              </span>
              <ChevronRight size={14} />
            </button>
          ))}
          {!findings.length && (
            <div className="empty-list">
              <Search size={22} />
              <strong>No matching findings</strong>
              <span>Change the severity filter or search terms.</span>
            </div>
          )}
        </div>
        <FindingDetail finding={selected} />
      </div>
    </div>
  );
}


function FindingDetail({ finding }: { finding: Finding | null }) {
  if (!finding) {
    return (
      <div className="finding-detail empty-detail">
        <Shield size={28} />
        <span>Select a finding to inspect its evidence.</span>
      </div>
    );
  }
  return (
    <article className="finding-detail">
      <div className="detail-header">
        <div className={`detail-emblem tone-${finding.severity}`}>
          {finding.severity === "critical" ? (
            <AlertOctagon size={22} />
          ) : (
            <AlertTriangle size={22} />
          )}
        </div>
        <div>
          <div className="detail-labels">
            <span className={`severity-label tone-${finding.severity}`}>
              {finding.severity}
            </span>
            <span>{finding.confidence} confidence</span>
          </div>
          <h2>{finding.title}</h2>
          <code>{finding.ruleId}</code>
        </div>
      </div>
      <section>
        <h3>Why this matters</h3>
        <p>{finding.description}</p>
      </section>
      <section>
        <h3>Location</h3>
        <div className="evidence-path">
          <FileCode2 size={15} />
          <code>{finding.entry}</code>
        </div>
      </section>
      <section>
        <h3>Matched evidence</h3>
        <div className="evidence-block">
          {finding.evidence.map((evidence, index) => (
            <div key={`${evidence}-${index}`}>
              <span>{String(index + 1).padStart(2, "0")}</span>
              <code>{evidence}</code>
            </div>
          ))}
        </div>
      </section>
      <section>
        <h3>Analyst action</h3>
        <div className="recommendation">
          <Info size={16} />
          <p>{finding.recommendation}</p>
        </div>
      </section>
      <div className="tag-row">
        {finding.tags.map((tag) => (
          <span key={tag}>#{tag}</span>
        ))}
      </div>
    </article>
  );
}
